import { ApiError, NetworkError } from '@/api/client';
import { statusLabel } from '@/components/StatusBadge';
import type { TranslateFn } from '@/i18n/LanguageContext';
import type { TranslationKey } from '@/i18n/dictionaries/en';
import type { CancelReasonCode, StockReasonCode } from '@/api/types';

/** A fresh idempotency key. One per dialog submission; the SAME one is reused when that submission is retried. */
export function newOpId(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  const hex = (n: number): string => Math.floor(Math.random() * 16 ** n).toString(16).padStart(n, '0');
  const variant = ['8', '9', 'a', 'b'][Math.floor(Math.random() * 4)] ?? '8';
  return `${hex(8)}-${hex(4)}-4${hex(3)}-${variant}${hex(3)}-${hex(12)}`;
}

const STOCK_REASON_KEYS: Record<StockReasonCode, TranslationKey> = {
  RECOUNT: 'reason.RECOUNT',
  FOUND: 'reason.FOUND',
  LOST: 'reason.LOST',
  DAMAGED: 'reason.DAMAGED',
  MISPLACED: 'reason.MISPLACED',
  EXPIRED: 'reason.EXPIRED',
  DATA_ERROR: 'reason.DATA_ERROR',
  OTHER: 'reason.OTHER',
};
const CANCEL_REASON_KEYS: Record<CancelReasonCode, TranslationKey> = {
  CLIENT_REQUEST: 'cancelReason.CLIENT_REQUEST',
  OUT_OF_STOCK: 'cancelReason.OUT_OF_STOCK',
  DUPLICATE: 'cancelReason.DUPLICATE',
  ENTERED_BY_MISTAKE: 'cancelReason.ENTERED_BY_MISTAKE',
  OTHER: 'cancelReason.OTHER',
};
export const stockReasonLabel = (t: TranslateFn, code: StockReasonCode): string => t(STOCK_REASON_KEYS[code]);
export const cancelReasonLabel = (t: TranslateFn, code: CancelReasonCode): string => t(CANCEL_REASON_KEYS[code]);

/** "10.0000" -> "10", "10.5000" -> "10.5". Display only - the original string is what goes back to the API. */
export function plainQty(v: string | number | null | undefined): string {
  const s = String(v ?? '0').trim();
  if (!/^-?\d+(\.\d+)?$/.test(s)) return s;
  const [i = '0', f = ''] = s.split('.');
  const frac = f.replace(/0+$/, '');
  return frac ? `${i}.${frac}` : i;
}

const SCALE = BigInt(10000);
/** Decimal string (scale 4) to an exact integer - never a float. */
export function toScaled(v: string): bigint {
  const s = v.trim();
  const neg = s.startsWith('-');
  const [i = '0', f = ''] = s.replace('-', '').split('.');
  const r = BigInt(i || '0') * SCALE + BigInt((f + '0000').slice(0, 4));
  return neg ? -r : r;
}
export function fromScaled(v: bigint): string {
  const neg = v < BigInt(0);
  const abs = neg ? -v : v;
  const i = abs / SCALE;
  const f = (abs % SCALE).toString().padStart(4, '0');
  return plainQty(`${neg ? '-' : ''}${i.toString()}.${f}`);
}
/** "+3" / "−3" / "0" */
export function signedQty(v: bigint): string {
  if (v === BigInt(0)) return '0';
  return v > BigInt(0) ? `+${fromScaled(v)}` : `−${fromScaled(-v)}`;
}
export function signedFromString(s: string): string {
  return signedQty(toScaled(s));
}

export function isWholeQty(s: string): boolean {
  return /^\d{1,9}$/.test(s.trim());
}

/** A sentence for any failure - never a raw code, UUID or request id. */
export function describeActionError(t: TranslateFn, err: unknown): string {
  if (err instanceof NetworkError) return t('err.network');
  if (!(err instanceof ApiError)) return t('err.generic');
  const p = err.params as Record<string, unknown>;
  const str = (k: string): string => plainQty(p[k] === undefined || p[k] === null ? '' : String(p[k]));
  switch (err.code) {
    case 'STOCK.COUNT_STALE':
      return t('err.countStale', { current: str('current') });
    case 'STOCK.INSUFFICIENT':
      return t('err.insufficient', { available: str('available'), requested: str('requested') });
    case 'STOCK.REASON_REQUIRED':
      return p.field === 'note' ? t('err.noteRequired') : t('err.reasonRequired');
    case 'STOCK.WRITE_OFF_OFFICE_ONLY':
      return t('err.writeOffOfficeOnly');
    case 'RECEIVING.ADJUST_BELOW_HELD':
      return t('err.adjustBelowHeld', { held: str('held'), onHand: str('onHand') });
    case 'RECEIVING.OVERAGE_HELD':
      return t('err.overageHeld', { held: str('held') });
    case 'STOCK.TRACKED_UNSUPPORTED':
      return t('err.trackedUnsupported');
    case 'STOCK.LOCATION_NOT_ALLOWED':
      if (p.reason === 'ARCHIVED') return t('err.locArchived');
      if (p.reason === 'OTHER_WAREHOUSE') return t('err.locOtherWarehouse');
      if (p.reason === 'NOT_QUARANTINE') return t('err.locNotQuarantine');
      return t('err.locationNotAllowed');
    case 'LOCATION.ARCHIVED':
      return t('err.locArchived');
    case 'QUANTITY.INVALID':
      return t('err.quantityInvalid');
    case 'COMMON.NOT_FOUND':
      return t('err.notFound');
    case 'COMMON.VALIDATION_FAILED':
      return t('err.validation');
    case 'ORDER.CANNOT_CANCEL':
      return t('err.cannotCancel', { state: statusLabel(t, typeof p.state === 'string' ? p.state : null) });
    case 'ORDER.CHANNEL_ORDER_FORBIDDEN':
      return t('err.channelForbidden');
    case 'ORDER.REALLOCATION_IMPOSSIBLE':
      return t('err.reallocImpossible');
    case 'ORDER.REDUCE_NOT_ALLOWED':
      return t('err.reduceNotAllowed');
    case 'ORDER.LINE_ALREADY_PICKED':
      return t('err.linePicked');
    case 'ORDER.LINE_CANCELLED':
      return t('err.lineCancelled');
    case 'ORDER.INVALID_STATE':
      return t('err.invalidState', { state: statusLabel(t, typeof p.state === 'string' ? p.state : null) });
    case 'RATE_LIMITED':
      return t('err.rateLimited');
    default:
      if (err.status === 429) return t('err.rateLimited');
      if (err.status === 403) return t('err.forbidden');
      return t('err.generic');
  }
}

export function ActionError({ message }: { message: string }): JSX.Element {
  return (
    <div className="error-banner" role="alert">
      {message}
    </div>
  );
}
